// src/pages/CohortClassroomPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { cohortService } from '../api/services';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Users, Video, PenLine, Plus, Search, Calendar, Clock,
  FileText, Upload, CheckCircle2, AlertCircle, X, Loader2,
  ArrowLeft, Award, ChevronRight, UserCheck, Shield, Trash2, Download, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';

const CohortClassroomPage = () => {
  const { id: cohortId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [cohort, setCohort] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('lectures'); // 'lectures' | 'assignments' | 'instructors' | 'students'

  // Data lists
  const [lectures, setLectures] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loadingLectures, setLoadingLectures] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);

  // Modals state
  const [showAddInstructor, setShowAddInstructor] = useState(false);
  const [showCreateLecture, setShowCreateLecture] = useState(false);
  const [showCreateAssignment, setShowCreateAssignment] = useState(false);
  const [submitModalAssignment, setSubmitModalAssignment] = useState(null);
  const [viewSubmissionsAssignment, setViewSubmissionsAssignment] = useState(null);
  const [viewAttendanceLecture, setViewAttendanceLecture] = useState(null);
  const [deletingLectureId, setDeletingLectureId] = useState(null);

  const isInstructor = user?.role === 'SUPER_ADMIN' || user?.role === 'EXPERT';
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isStudent = user?.role === 'CLIENT';

  const handleDeleteLecture = async (lectureId, lectureTitle) => {
    if (!window.confirm(`Are you sure you want to delete the lecture video call "${lectureTitle}"?\n\nThis will permanently delete the meeting room and all associated attendance records.`)) {
      return;
    }
    setDeletingLectureId(lectureId);
    try {
      await cohortService.deleteLecture(cohortId, lectureId);
      toast.success('Lecture video call deleted successfully');
      loadLectures();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not delete lecture');
    } finally {
      setDeletingLectureId(null);
    }
  };

  const loadCohort = async () => {
    try {
      const res = await cohortService.getById(cohortId);
      const c = res.data?.data?.cohort ?? res.data?.cohort;
      setCohort(c);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load cohort');
    } finally {
      setLoading(false);
    }
  };

  const loadLectures = async () => {
    setLoadingLectures(true);
    try {
      const res = await cohortService.getLectures(cohortId);
      setLectures(res.data?.data?.lectures ?? res.data?.lectures ?? []);
    } catch {
      setLectures([]);
    } finally {
      setLoadingLectures(false);
    }
  };

  const loadAssignments = async () => {
    setLoadingAssignments(true);
    try {
      const res = await cohortService.getAssignments(cohortId);
      setAssignments(res.data?.data?.assignments ?? res.data?.assignments ?? []);
    } catch {
      setAssignments([]);
    } finally {
      setLoadingAssignments(false);
    }
  };

  useEffect(() => {
    loadCohort();
  }, [cohortId]);

  useEffect(() => {
    if (activeTab === 'lectures') loadLectures();
    if (activeTab === 'assignments') loadAssignments();
  }, [cohortId, activeTab]);

  if (loading) {
    return (
      <DashboardLayout title="Classroom">
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-forest-500" />
        </div>
      </DashboardLayout>
    );
  }

  if (!cohort) {
    return (
      <DashboardLayout title="Classroom">
        <div className="bg-white rounded-2xl p-8 border border-cream-200 text-center space-y-3">
          <AlertCircle size={36} className="mx-auto text-rust-500" />
          <h3 className="font-serif text-lg text-forest-900">Cohort not found</h3>
          <button onClick={() => navigate(-1)} className="btn-secondary text-xs">Go Back</button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={cohort.name}>
      <div className="space-y-6 max-w-6xl">

        {/* Back Link */}
        <button
          onClick={() => navigate(user?.role === 'EXPERT' ? '/expert/cohorts' : user?.role === 'SUPER_ADMIN' ? '/admin/cohorts' : '/dashboard')}
          className="inline-flex items-center gap-1.5 text-xs text-forest-500 hover:text-forest-800 transition-colors"
        >
          <ArrowLeft size={13} /> Back to {user?.role === 'EXPERT' ? 'My Cohorts' : user?.role === 'SUPER_ADMIN' ? 'Cohorts' : 'Dashboard'}
        </button>

        {/* Cohort Header Banner */}
        <div className="bg-gradient-to-r from-forest-900 to-forest-800 text-cream-50 rounded-2xl p-6 md:p-8 shadow-sm relative overflow-hidden">
          <div className="relative z-10 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider bg-green-500/20 text-green-300 border border-green-500/30 px-3 py-0.5 rounded-full">
                Active Cohort
              </span>
              {cohort.stats && (
                <span className="text-[10px] text-cream-200/70">
                  {cohort.stats.studentsCount} Student{cohort.stats.studentsCount !== 1 ? 's' : ''} · {cohort.stats.instructorsCount} Instructor{cohort.stats.instructorsCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            <h1 className="font-serif text-2xl md:text-3xl font-bold text-white tracking-tight">
              {cohort.name}
            </h1>

            {cohort.description && (
              <p className="text-xs md:text-sm text-cream-200/80 max-w-2xl leading-relaxed">
                {cohort.description}
              </p>
            )}

            {/* Quick Actions for Instructors */}
            {isInstructor && (
              <div className="pt-2 flex flex-wrap gap-2.5">
                <button
                  onClick={() => setShowCreateLecture(true)}
                  className="btn-primary text-xs py-2 px-3.5 bg-green-600 hover:bg-green-500 border-0 flex items-center gap-1.5"
                >
                  <Video size={13} /> Schedule Lecture
                </button>
                <button
                  onClick={() => setShowCreateAssignment(true)}
                  className="btn-secondary text-xs py-2 px-3.5 bg-white/10 text-white hover:bg-white/20 border-white/20 flex items-center gap-1.5"
                >
                  <PenLine size={13} /> Create Assignment
                </button>
                <button
                  onClick={() => setShowAddInstructor(true)}
                  className="btn-secondary text-xs py-2 px-3.5 bg-white/10 text-white hover:bg-white/20 border-white/20 flex items-center gap-1.5"
                >
                  <UserCheck size={13} /> Add Guest Lecturer / TA
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex items-center gap-1 border-b border-cream-200 overflow-x-auto pb-px">
          {[
            { id: 'lectures',     label: 'Online Lectures',       icon: Video, count: lectures.length },
            { id: 'assignments',  label: 'Coursework / Assignments', icon: PenLine, count: assignments.length },
            { id: 'instructors',  label: 'Lecturers & TAs',       icon: Award, count: cohort.instructors?.length },
            { id: 'students',     label: 'Enrolled Students',     icon: Users, count: cohort.students?.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold rounded-t-xl transition-all border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-forest-800 text-forest-900 bg-white'
                  : 'border-transparent text-forest-500 hover:text-forest-800 hover:bg-cream-100/50'
              }`}
            >
              <tab.icon size={14} />
              {tab.label}
              {tab.count !== undefined && (
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full ${activeTab === tab.id ? 'bg-forest-100 text-forest-800' : 'bg-cream-200 text-forest-500'}`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── TAB 1: ONLINE LECTURES ─────────────────────────────── */}
        {activeTab === 'lectures' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif text-base font-semibold text-forest-900">Live Online Lectures</h3>
                <p className="text-xs text-forest-400">Integrated video classroom with video, audio, screen share, and chat.</p>
              </div>
              {isInstructor && (
                <button onClick={() => setShowCreateLecture(true)} className="btn-primary text-xs py-2 px-3 flex items-center gap-1.5">
                  <Plus size={13} /> Schedule Lecture
                </button>
              )}
            </div>

            {loadingLectures ? (
              <div className="py-12 text-center"><Loader2 size={24} className="animate-spin mx-auto text-forest-400" /></div>
            ) : lectures.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 border border-cream-200 text-center space-y-2">
                <Video size={36} className="mx-auto text-forest-300" />
                <h4 className="font-serif text-sm font-semibold text-forest-800">No lectures scheduled yet</h4>
                <p className="text-xs text-forest-400 max-w-sm mx-auto">
                  {isInstructor
                    ? 'Schedule the first live online lecture for this cohort. Students will be notified automatically.'
                    : 'Your instructors have not scheduled any live lectures yet. Check back soon.'}
                </p>
                {isInstructor && (
                  <button onClick={() => setShowCreateLecture(true)} className="btn-primary text-xs mt-3 inline-flex items-center gap-1.5">
                    <Plus size={13} /> Schedule Lecture
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {lectures.map((lec) => {
                  const dt = lec.scheduledAt ? new Date(lec.scheduledAt) : null;
                  const isPast = dt && dt.getTime() + (lec.duration || 60) * 60000 < Date.now();
                  return (
                    <div key={lec.id} className="bg-white rounded-2xl p-5 border border-cream-200 space-y-3 flex flex-col justify-between shadow-sm hover:border-forest-300 transition-all">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full ${
                            isPast ? 'bg-cream-100 text-forest-400' : 'bg-green-50 text-green-700 border border-green-200'
                          }`}>
                            {isPast ? 'Past' : 'Upcoming'}
                          </span>
                          <div className="flex items-center gap-2 text-xs text-forest-400">
                            <span className="flex items-center gap-1 text-forest-600 font-medium">
                              <Users size={12} className="text-forest-400" /> {lec.attendeeCount || 0} joined
                            </span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Clock size={11} /> {lec.duration}m
                            </span>
                          </div>
                        </div>

                        <h4 className="font-serif font-bold text-forest-900 text-base">{lec.title}</h4>
                        {lec.description && (
                          <p className="text-xs text-forest-600 line-clamp-2 leading-relaxed">{lec.description}</p>
                        )}
                      </div>

                      <div className="pt-2 border-t border-cream-100 flex items-center justify-between text-xs flex-wrap gap-2">
                        <span className="text-forest-500 font-medium flex items-center gap-1">
                          <Calendar size={12} /> {dt ? dt.toLocaleString('en-GH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'TBD'}
                        </span>

                        <div className="flex items-center gap-1.5 flex-wrap">
                          {isInstructor && (
                            <button
                              onClick={() => setViewAttendanceLecture(lec)}
                              className="text-xs py-1.5 px-2.5 rounded-xl border border-forest-200 text-forest-700 hover:bg-forest-50 flex items-center gap-1 font-medium transition-colors"
                              title="View attendance list"
                            >
                              <Users size={12} /> Attendance
                            </button>
                          )}

                          {(isSuperAdmin || lec.createdById === user?.id) && (
                            <button
                              onClick={() => handleDeleteLecture(lec.id, lec.title)}
                              disabled={deletingLectureId === lec.id}
                              className="text-xs py-1.5 px-2.5 rounded-xl border border-rust-200 text-rust-600 hover:bg-rust-50 flex items-center gap-1 font-medium transition-colors disabled:opacity-50"
                              title="Delete lecture video call"
                            >
                              {deletingLectureId === lec.id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                              Delete
                            </button>
                          )}

                          <Link
                            to={`/cohorts/${cohortId}/lecture/${lec.id}`}
                            className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1"
                          >
                            <Video size={12} /> Join Lecture
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TAB 2: COURSEWORK & ASSIGNMENTS ────────────────────── */}
        {activeTab === 'assignments' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif text-base font-semibold text-forest-900">Cohort Coursework & Assignments</h3>
                <p className="text-xs text-forest-400">Class projects, continuous assessments, and IA tasks for this cohort.</p>
              </div>
              {isInstructor && (
                <button onClick={() => setShowCreateAssignment(true)} className="btn-primary text-xs py-2 px-3 flex items-center gap-1.5">
                  <Plus size={13} /> Create Assignment
                </button>
              )}
            </div>

            {loadingAssignments ? (
              <div className="py-12 text-center"><Loader2 size={24} className="animate-spin mx-auto text-forest-400" /></div>
            ) : assignments.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 border border-cream-200 text-center space-y-2">
                <PenLine size={36} className="mx-auto text-forest-300" />
                <h4 className="font-serif text-sm font-semibold text-forest-800">No assignments posted yet</h4>
                <p className="text-xs text-forest-400 max-w-sm mx-auto">
                  {isInstructor
                    ? 'Create an assignment with instructions, submission guidelines, and due dates.'
                    : 'No assignments have been assigned to your cohort yet.'}
                </p>
                {isInstructor && (
                  <button onClick={() => setShowCreateAssignment(true)} className="btn-primary text-xs mt-3 inline-flex items-center gap-1.5">
                    <Plus size={13} /> Create Assignment
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {assignments.map((asg) => {
                  const dueDateStr = asg.dueDate ? new Date(asg.dueDate).toLocaleDateString('en-GH', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
                  const mySub = asg.mySubmission;

                  return (
                    <div key={asg.id} className="bg-white rounded-2xl p-5 border border-cream-200 space-y-3 hover:border-forest-300 transition-all">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-serif font-bold text-forest-900 text-base">{asg.title}</h4>
                            <span className="text-[10px] text-forest-500 bg-cream-100 px-2 py-0.5 rounded-full">
                              Max score: {asg.maxScore}
                            </span>
                          </div>

                          {asg.description && (
                            <p className="text-xs text-forest-600 leading-relaxed">{asg.description}</p>
                          )}
                          {asg.instructions && (
                            <div className="bg-cream-50 rounded-xl p-3 text-xs text-forest-700 leading-relaxed mt-2 whitespace-pre-line border border-cream-100">
                              <span className="font-bold text-[10px] uppercase text-forest-500 block mb-1">Instructions:</span>
                              {asg.instructions}
                            </div>
                          )}
                        </div>

                        {/* Status & Action */}
                        <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 flex-shrink-0">
                          {dueDateStr && (
                            <span className="text-[11px] text-forest-400 flex items-center gap-1">
                              <Calendar size={11} /> Due: {dueDateStr}
                            </span>
                          )}

                          {isInstructor ? (
                            <button
                              onClick={() => setViewSubmissionsAssignment(asg)}
                              className="btn-secondary text-xs py-1.5 px-3 flex items-center gap-1 font-medium"
                            >
                              <Users size={12} /> {asg.submissionCount || 0} Submission{asg.submissionCount !== 1 ? 's' : ''}
                            </button>
                          ) : (
                            <div>
                              {mySub ? (
                                <div className="text-right space-y-1">
                                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full inline-block ${
                                    mySub.status === 'GRADED' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
                                  }`}>
                                    {mySub.status === 'GRADED' ? `Score: ${mySub.score}/${asg.maxScore}` : 'Submitted'}
                                  </span>
                                  {mySub.feedback && (
                                    <p className="text-[11px] text-forest-600 italic max-w-xs text-left">
                                      "{mySub.feedback}"
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <button
                                  onClick={() => setSubmitModalAssignment(asg)}
                                  className="btn-primary text-xs py-1.5 px-3.5 flex items-center gap-1"
                                >
                                  <Upload size={12} /> Submit Work
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TAB 3: INSTRUCTORS & TEACHING ASSISTANTS ─────────────── */}
        {activeTab === 'instructors' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-serif text-base font-semibold text-forest-900">Lecturers & Teaching Assistants</h3>
                <p className="text-xs text-forest-400">Instructors assigned to teach, organise lectures, and grade this cohort.</p>
              </div>
              {isInstructor && (
                <button onClick={() => setShowAddInstructor(true)} className="btn-primary text-xs py-2 px-3 flex items-center gap-1.5">
                  <Plus size={13} /> Add Lecturer / TA
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(cohort.instructors || []).map((m) => {
                const u = m.user;
                const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
                const roleTitle = m.cohortRole === 'PRIMARY_EXPERT'
                  ? 'Lecturer'
                  : m.cohortRole === 'TEACHING_ASSISTANT'
                  ? 'Teaching Assistant'
                  : 'Guest Lecturer';

                return (
                  <div key={m.id || u.id} className="bg-white rounded-2xl p-4 border border-cream-200 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-forest-800 text-cream-50 font-bold text-sm flex items-center justify-center">
                        {name[0] || 'E'}
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="font-bold text-forest-900 text-sm">{name}</h4>
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.2 rounded-full ${
                            m.cohortRole === 'PRIMARY_EXPERT'
                              ? 'bg-amber-100 text-amber-800'
                              : m.cohortRole === 'TEACHING_ASSISTANT'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-forest-100 text-forest-800'
                          }`}>
                            {roleTitle}
                          </span>
                        </div>
                        <p className="text-xs text-forest-400">{u.email}</p>
                      </div>
                    </div>

                    {isInstructor && user.id !== u.id && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Remove ${name} from this cohort?`)) return;
                          try {
                            await cohortService.removeMember(cohortId, u.id);
                            toast.success('Removed instructor');
                            loadCohort();
                          } catch {
                            toast.error('Could not remove');
                          }
                        }}
                        className="text-xs text-rust-500 hover:text-rust-700 p-2"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── TAB 4: ENROLLED STUDENTS ───────────────────────────── */}
        {activeTab === 'students' && (
          <div className="space-y-4">
            <div>
              <h3 className="font-serif text-base font-semibold text-forest-900">Enrolled Students ({cohort.students?.length || 0})</h3>
              <p className="text-xs text-forest-400">All students registered under this cohort.</p>
            </div>

            {cohort.students?.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 border border-cream-200 text-center text-forest-400 text-xs">
                No students enrolled in this cohort yet.
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-cream-200 overflow-hidden divide-y divide-cream-100">
                {cohort.students?.map((m) => {
                  const u = m.user;
                  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
                  return (
                    <div key={m.id || u.id} className="p-4 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-forest-100 text-forest-800 font-bold flex items-center justify-center">
                          {name[0] || 'S'}
                        </div>
                        <div>
                          <p className="font-bold text-forest-900">{name}</p>
                        </div>
                      </div>

                      {user?.role === 'SUPER_ADMIN' && (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Remove ${name} from this cohort?`)) return;
                            try {
                              await cohortService.removeMember(cohortId, u.id);
                              toast.success('Student removed from cohort');
                              loadCohort();
                            } catch {
                              toast.error('Could not remove');
                            }
                          }}
                          className="text-rust-500 hover:text-rust-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── MODAL 1: ADD INSTRUCTOR (GUEST LECTURER / TA) ──────────── */}
        {showAddInstructor && (
          <AddInstructorModal
            cohortId={cohortId}
            onClose={() => setShowAddInstructor(false)}
            onAdded={() => {
              setShowAddInstructor(false);
              loadCohort();
            }}
          />
        )}

        {/* ── MODAL 2: SCHEDULE LECTURE ──────────────────────────── */}
        {showCreateLecture && (
          <CreateLectureModal
            cohortId={cohortId}
            onClose={() => setShowCreateLecture(false)}
            onCreated={() => {
              setShowCreateLecture(false);
              loadLectures();
              loadCohort();
            }}
          />
        )}

        {/* ── MODAL 3: CREATE ASSIGNMENT ─────────────────────────── */}
        {showCreateAssignment && (
          <CreateAssignmentModal
            cohortId={cohortId}
            onClose={() => setShowCreateAssignment(false)}
            onCreated={() => {
              setShowCreateAssignment(false);
              loadAssignments();
              loadCohort();
            }}
          />
        )}

        {/* ── MODAL 4: SUBMIT ASSIGNMENT (STUDENT) ───────────────── */}
        {submitModalAssignment && (
          <SubmitAssignmentModal
            cohortId={cohortId}
            assignment={submitModalAssignment}
            onClose={() => setSubmitModalAssignment(null)}
            onSubmitted={() => {
              setSubmitModalAssignment(null);
              loadAssignments();
            }}
          />
        )}

        {/* ── MODAL 5: VIEW & GRADE SUBMISSIONS (INSTRUCTOR) ─────── */}
        {viewSubmissionsAssignment && (
          <ViewSubmissionsModal
            cohortId={cohortId}
            assignment={viewSubmissionsAssignment}
            onClose={() => setViewSubmissionsAssignment(null)}
            onGraded={() => loadAssignments()}
          />
        )}

        {/* ── MODAL 6: VIEW LECTURE ATTENDANCE ───────────────────── */}
        {viewAttendanceLecture && (
          <LectureAttendanceModal
            cohortId={cohortId}
            lecture={viewAttendanceLecture}
            onClose={() => setViewAttendanceLecture(null)}
          />
        )}

      </div>
    </DashboardLayout>
  );
};

// ── SUB-MODALS ─────────────────────────────────────────────────

const AddInstructorModal = ({ cohortId, onClose, onAdded }) => {
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('CO_EXPERT');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [saving, setSaving] = useState(false);

  const searchExperts = async (q) => {
    setQuery(q);
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await cohortService.searchAvailableExperts(cohortId, q.trim());
      setResults(res.data?.data?.experts ?? res.data?.experts ?? []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedExpert) { toast.error('Please select an expert'); return; }
    setSaving(true);
    try {
      await cohortService.assignInstructor(cohortId, { userId: selectedExpert.id, role });
      toast.success('Instructor assigned to cohort!');
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not assign instructor');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-cream-200 pb-3">
          <h3 className="font-serif text-lg font-bold text-forest-900">Add Lecturer or Teaching Assistant</h3>
          <button onClick={onClose}><X size={18} className="text-forest-400" /></button>
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Instructor Role</label>
          <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300">
            <option value="CO_EXPERT">Guest Lecturer</option>
            <option value="TEACHING_ASSISTANT">Teaching Assistant (TA)</option>
            <option value="PRIMARY_EXPERT">Lecturer</option>
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1.5">Search Verified Experts</label>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest-400" />
            <input
              value={query}
              onChange={(e) => searchExperts(e.target.value)}
              placeholder="Search expert by name or email..."
              className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-cream-300"
            />
          </div>
          {searching && <Loader2 size={14} className="animate-spin text-forest-400 mt-2" />}

          {results.length > 0 && (
            <div className="mt-2 divide-y divide-cream-100 border border-cream-200 rounded-xl max-h-48 overflow-y-auto">
              {results.map((e) => (
                <div
                  key={e.id}
                  onClick={() => setSelectedExpert(e)}
                  className={`p-2.5 text-xs flex items-center justify-between cursor-pointer transition-colors ${
                    selectedExpert?.id === e.id ? 'bg-forest-50 border-forest-500' : 'hover:bg-cream-50'
                  }`}
                >
                  <div>
                    <p className="font-bold text-forest-900">{e.firstName} {e.lastName}</p>
                    <p className="text-[10px] text-forest-400">{e.email}</p>
                  </div>
                  {selectedExpert?.id === e.id && <CheckCircle2 size={14} className="text-forest-700" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedExpert && (
          <div className="bg-forest-50 rounded-xl p-3 text-xs text-forest-800 border border-forest-100">
            Assigning <strong>{selectedExpert.firstName} {selectedExpert.lastName}</strong> as <strong>{role.replace('_', ' ')}</strong>.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-cream-200">
          <button onClick={onClose} className="btn-secondary text-xs py-2 px-3">Cancel</button>
          <button onClick={handleAssign} disabled={saving || !selectedExpert} className="btn-primary text-xs py-2 px-4">
            {saving ? 'Assigning...' : 'Assign to Cohort'}
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateLectureModal = ({ cohortId, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: '', description: '', scheduledAt: '', duration: 60 });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Lecture title is required'); return; }
    if (!form.scheduledAt) { toast.error('Date and time required'); return; }
    setSaving(true);
    try {
      await cohortService.createLecture(cohortId, form);
      toast.success('Online lecture scheduled! Students notified.');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not schedule lecture');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm">
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-cream-200 pb-3">
          <h3 className="font-serif text-lg font-bold text-forest-900">Schedule Online Lecture</h3>
          <button type="button" onClick={onClose}><X size={18} className="text-forest-400" /></button>
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1">Lecture Title *</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Relationship Marketing — Week 3 Lecture"
            className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1">Description / Topic Outline</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What will be covered in this lecture..."
            rows={3}
            className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300 resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">Date & Time *</label>
            <input
              type="datetime-local"
              value={form.scheduledAt}
              onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
              className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">Duration (minutes)</label>
            <input
              type="number"
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300"
            />
          </div>
        </div>

        <p className="text-[11px] text-forest-400 bg-forest-50 p-2.5 rounded-xl border border-forest-100">
          A built-in video classroom room will be generated automatically. All cohort students will receive in-app and SMS alerts.
        </p>

        <div className="flex justify-end gap-2 pt-2 border-t border-cream-200">
          <button type="button" onClick={onClose} className="btn-secondary text-xs py-2 px-3">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary text-xs py-2 px-4">
            {saving ? 'Scheduling...' : 'Schedule Lecture'}
          </button>
        </div>
      </form>
    </div>
  );
};

const CreateAssignmentModal = ({ cohortId, onClose, onCreated }) => {
  const [form, setForm] = useState({
    title: '', description: '', instructions: '', dueDate: '', maxScore: 100, passingScore: 60
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await cohortService.createAssignment(cohortId, form);
      toast.success('Assignment created! Students notified.');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not create assignment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm">
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-cream-200 pb-3">
          <h3 className="font-serif text-lg font-bold text-forest-900">Create Cohort Assignment / IA</h3>
          <button type="button" onClick={onClose}><X size={18} className="text-forest-400" /></button>
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1">Assignment Title *</label>
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Semester IA: Relationship Marketing Audit Project"
            className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1">Summary / Context</label>
          <input
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Brief overview of what this coursework covers"
            className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1">Instructions & Rubric</label>
          <textarea
            value={form.instructions}
            onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            placeholder="Step-by-step instructions, questions, required deliverables..."
            rows={4}
            className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300 resize-none"
          />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">Due Date</label>
            <input
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">Max Score</label>
            <input
              type="number"
              value={form.maxScore}
              onChange={(e) => setForm({ ...form, maxScore: e.target.value })}
              className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-forest-700 mb-1">Passing Score</label>
            <input
              type="number"
              value={form.passingScore}
              onChange={(e) => setForm({ ...form, passingScore: e.target.value })}
              className="w-full px-3 py-2 text-xs rounded-xl border border-cream-300"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-cream-200">
          <button type="button" onClick={onClose} className="btn-secondary text-xs py-2 px-3">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary text-xs py-2 px-4">
            {saving ? 'Creating...' : 'Post Assignment'}
          </button>
        </div>
      </form>
    </div>
  );
};

const SubmitAssignmentModal = ({ cohortId, assignment, onClose, onSubmitted }) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const handleFileAdd = (e) => {
    const newFiles = Array.from(e.target.files ?? []);
    setFiles((prev) => [...prev, ...newFiles].slice(0, 5));
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!text.trim() && files.length === 0) {
      toast.error('Please enter written submission text or attach a file.');
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('submissionText', text);
      files.forEach((f) => formData.append('files', f));

      await cohortService.submitAssignment(cohortId, assignment.id, formData);
      toast.success('Assignment submitted successfully!');
      onSubmitted();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm">
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-cream-200 pb-3">
          <h3 className="font-serif text-lg font-bold text-forest-900">Submit: {assignment.title}</h3>
          <button type="button" onClick={onClose}><X size={18} className="text-forest-400" /></button>
        </div>

        <div>
          <label className="block text-xs font-medium text-forest-700 mb-1">Written Submission Text</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type or paste your coursework answers here directly..."
            rows={5}
            className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-cream-300 resize-none"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs font-medium text-forest-700">Attach Document / Presentation / Project File</label>
            <button type="button" onClick={() => fileRef.current?.click()} className="text-xs text-forest-700 hover:underline">
              + Add File
            </button>
          </div>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept=".pdf,.docx,.doc,.pptx,.ppt,.png,.jpg,.jpeg,.zip"
            onChange={handleFileAdd}
            className="hidden"
          />

          {files.length > 0 ? (
            <div className="space-y-1.5">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-2 bg-cream-50 border border-cream-200 rounded-xl text-xs">
                  <span className="truncate max-w-xs">{f.name}</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, idx) => idx !== i))} className="text-rust-500">
                    <X size={13} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="w-full border-2 border-dashed border-cream-300 rounded-xl p-5 text-center text-xs text-forest-400 hover:border-forest-500 transition-colors"
            >
              Upload PDF, DOCX, or Presentation Slides (PPTX)
            </button>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-cream-200">
          <button type="button" onClick={onClose} className="btn-secondary text-xs py-2 px-3">Cancel</button>
          <button type="submit" disabled={submitting} className="btn-primary text-xs py-2 px-4">
            {submitting ? 'Submitting...' : 'Submit Work'}
          </button>
        </div>
      </form>
    </div>
  );
};

const ViewSubmissionsModal = ({ cohortId, assignment, onClose, onGraded }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSub, setSelectedSub] = useState(null);
  const [score, setScore] = useState('');
  const [feedback, setFeedback] = useState('');
  const [grading, setGrading] = useState(false);

  useEffect(() => {
    cohortService.getSubmissions(cohortId, assignment.id)
      .then((res) => {
        setSubmissions(res.data?.data?.submissions ?? res.data?.submissions ?? []);
      })
      .catch(() => setSubmissions([]))
      .finally(() => setLoading(false));
  }, [cohortId, assignment.id]);

  const handleGrade = async () => {
    if (score === '') { toast.error('Score is required'); return; }
    setGrading(true);
    try {
      await cohortService.gradeSubmission(cohortId, assignment.id, selectedSub.id, {
        score: Number(score),
        feedback,
      });
      toast.success('Graded successfully!');
      setSelectedSub(null);
      onGraded();
      // reload submissions
      const res = await cohortService.getSubmissions(cohortId, assignment.id);
      setSubmissions(res.data?.data?.submissions ?? res.data?.submissions ?? []);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save grade');
    } finally {
      setGrading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl p-6 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-cream-200 pb-3">
          <div>
            <h3 className="font-serif text-lg font-bold text-forest-900">Submissions: {assignment.title}</h3>
            <p className="text-xs text-forest-400">Total submitted: {submissions.length}</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-forest-400" /></button>
        </div>

        {loading ? (
          <div className="py-10 text-center"><Loader2 size={24} className="animate-spin mx-auto text-forest-400" /></div>
        ) : submissions.length === 0 ? (
          <p className="text-xs text-forest-400 text-center py-8 italic">No students have submitted this assignment yet.</p>
        ) : (
          <div className="divide-y divide-cream-100 border border-cream-200 rounded-xl overflow-hidden">
            {submissions.map((s) => {
              let urls = [];
              try { urls = typeof s.fileUrls === 'string' ? JSON.parse(s.fileUrls) : s.fileUrls || []; } catch {}

              return (
                <div key={s.id} className="p-4 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-bold text-forest-900">{s.firstName} {s.lastName}</span>
                      <span className="text-forest-400 ml-2">({s.email})</span>
                    </div>
                    <div>
                      {s.status === 'GRADED' ? (
                        <span className="text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full font-bold">
                          Score: {s.score} / {assignment.maxScore}
                        </span>
                      ) : (
                        <button
                          onClick={() => { setSelectedSub(s); setScore(s.score ?? ''); setFeedback(s.feedback ?? ''); }}
                          className="btn-primary text-xs py-1 px-3"
                        >
                          Grade Student
                        </button>
                      )}
                    </div>
                  </div>

                  {s.submissionText && (
                    <div className="bg-cream-50 rounded-xl p-3 text-forest-700 whitespace-pre-line border border-cream-100">
                      {s.submissionText}
                    </div>
                  )}

                  {urls.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {urls.map((u, i) => (
                        <a key={i} href={u} target="_blank" rel="noopener noreferrer" className="btn-secondary text-[11px] py-1 px-2.5 flex items-center gap-1">
                          <FileText size={11} /> Attachment {i + 1}
                        </a>
                      ))}
                    </div>
                  )}

                  {s.feedback && (
                    <p className="text-forest-500 italic">Feedback: "{s.feedback}"</p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Grading Drawer / Box */}
        {selectedSub && (
          <div className="bg-forest-50 border border-forest-200 rounded-xl p-4 space-y-3">
            <h4 className="font-bold text-forest-900 text-xs">
              Grade {selectedSub.firstName} {selectedSub.lastName}
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] text-forest-600 mb-1">Score (out of {assignment.maxScore})</label>
                <input
                  type="number"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  className="w-full px-3 py-1.5 text-xs rounded-xl border border-cream-300"
                />
              </div>
              <div>
                <label className="block text-[11px] text-forest-600 mb-1">Feedback Comments</label>
                <input
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="e.g. Excellent analysis and structured recommendations."
                  className="w-full px-3 py-1.5 text-xs rounded-xl border border-cream-300"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setSelectedSub(null)} className="btn-secondary text-xs py-1 px-3">Cancel</button>
              <button onClick={handleGrade} disabled={grading} className="btn-primary text-xs py-1 px-3">
                {grading ? 'Saving...' : 'Save Grade'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── MODAL: LECTURE ATTENDANCE BREAKDOWN ─────────────────────────
const LectureAttendanceModal = ({ cohortId, lecture, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  const loadAttendance = async () => {
    setLoading(true);
    try {
      const res = await cohortService.getLectureAttendance(cohortId, lecture.id);
      setData(res.data?.data ?? res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load lecture attendance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttendance();
  }, [cohortId, lecture.id]);

  const records = data?.records || [];
  const uniqueAttendees = data?.uniqueAttendees || 0;
  const totalRecords = data?.totalRecords || 0;

  const filtered = records.filter((r) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (r.userName && r.userName.toLowerCase().includes(q)) ||
      (r.userEmail && r.userEmail.toLowerCase().includes(q)) ||
      (r.userRole && r.userRole.toLowerCase().includes(q))
    );
  });

  const handleExportCsv = () => {
    if (!records.length) {
      toast.error('No attendance records to export');
      return;
    }
    const headers = ['Name', 'Email', 'Role', 'Time Joined', 'Time Left', 'Duration (mins)'];
    const rows = records.map((r) => [
      `"${(r.userName || '').replace(/"/g, '""')}"`,
      `"${(r.userEmail || '').replace(/"/g, '""')}"`,
      `"${(r.userRole || '').replace(/"/g, '""')}"`,
      `"${r.joinedAt ? new Date(r.joinedAt).toLocaleString() : ''}"`,
      `"${r.leftAt ? new Date(r.leftAt).toLocaleString() : ''}"`,
      `"${r.durationMinutes ?? 0}"`,
    ]);
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `lecture-attendance-${lecture.id.slice(0, 8)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getRoleBadge = (role) => {
    if (role === 'SUPER_ADMIN') {
      return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">Super Admin</span>;
    }
    if (role === 'EXPERT') {
      return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Resource Person</span>;
    }
    return <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-forest-100 text-forest-700">Trainee</span>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-forest-900/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-cream-200 flex items-center justify-between bg-cream-50/60">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-forest-100 text-forest-800">
                Attendance Record
              </span>
              <h3 className="font-serif text-lg font-bold text-forest-900">{lecture.title}</h3>
            </div>
            <p className="text-xs text-forest-500 mt-0.5">
              Scheduled: {lecture.scheduledAt ? new Date(lecture.scheduledAt).toLocaleString('en-GH', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'TBD'} · {lecture.duration || 60}m duration
            </p>
          </div>
          <button onClick={onClose} className="p-1 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-cream-100 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-forest-50 border border-forest-100 rounded-xl p-3.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-forest-600 block">Unique People Joined</span>
              <p className="font-serif text-2xl font-bold text-forest-900 mt-1">{loading ? '—' : uniqueAttendees}</p>
            </div>
            <div className="bg-cream-50 border border-cream-200 rounded-xl p-3.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-forest-500 block">Total Join Events</span>
              <p className="font-serif text-2xl font-bold text-forest-900 mt-1">{loading ? '—' : totalRecords}</p>
            </div>
            <div className="bg-cream-50 border border-cream-200 rounded-xl p-3.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-forest-500 block">Duration</span>
              <p className="font-serif text-2xl font-bold text-forest-900 mt-1">{lecture.duration || 60} mins</p>
            </div>
          </div>

          {/* Search & Actions */}
          <div className="flex items-center justify-between gap-3">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-forest-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search attendee by name, email, or role..."
                className="w-full pl-9 pr-3 py-2 text-xs rounded-xl border border-cream-300 focus:outline-none focus:border-forest-500 bg-white"
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={loadAttendance}
                disabled={loading}
                className="p-2 border border-cream-200 rounded-xl text-forest-600 hover:bg-cream-100 transition-colors"
                title="Refresh Attendance"
              >
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              </button>
              <button
                onClick={handleExportCsv}
                disabled={!records.length}
                className="btn-secondary text-xs py-2 px-3 flex items-center gap-1.5 disabled:opacity-40"
              >
                <Download size={13} /> Export CSV
              </button>
            </div>
          </div>

          {/* Table */}
          {loading ? (
            <div className="py-16 text-center">
              <Loader2 size={24} className="animate-spin text-forest-500 mx-auto" />
              <p className="text-xs text-forest-400 mt-2">Loading attendance records...</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center bg-cream-50/50 rounded-2xl border border-cream-200">
              <Users size={32} className="mx-auto text-forest-300 mb-2" />
              <h4 className="font-serif text-sm font-semibold text-forest-800">
                {records.length === 0 ? 'No Attendees Joined Yet' : 'No matching attendees'}
              </h4>
              <p className="text-xs text-forest-400 max-w-xs mx-auto mt-1">
                {records.length === 0
                  ? 'When participants enter this lecture video call, their name, joined time, and left time will appear here automatically.'
                  : 'Try searching with a different name or email.'}
              </p>
            </div>
          ) : (
            <div className="border border-cream-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-cream-100/70 border-b border-cream-200 text-forest-700 font-semibold uppercase text-[10px] tracking-wider">
                    <tr>
                      <th className="py-2.5 px-4">Attendee Name</th>
                      <th className="py-2.5 px-3">Role</th>
                      <th className="py-2.5 px-3">Time Joined</th>
                      <th className="py-2.5 px-3">Time Left</th>
                      <th className="py-2.5 px-4 text-right">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cream-100">
                    {filtered.map((r) => {
                      const joinedDt = r.joinedAt ? new Date(r.joinedAt) : null;
                      const leftDt = r.leftAt ? new Date(r.leftAt) : null;
                      const isStillActive = leftDt && (Date.now() - leftDt.getTime() < 60000);

                      return (
                        <tr key={r.id} className="hover:bg-cream-50/60 transition-colors">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2.5">
                              <div className="w-7 h-7 rounded-full bg-forest-800 text-cream-50 font-bold text-[10px] flex items-center justify-center flex-shrink-0">
                                {r.userName?.[0] || 'A'}
                              </div>
                              <div className="min-w-0">
                                <p className="font-semibold text-forest-900 truncate">{r.userName || 'Unknown'}</p>
                                {r.userEmail && <p className="text-[10px] text-forest-400 truncate">{r.userEmail}</p>}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            {getRoleBadge(r.userRole)}
                          </td>
                          <td className="py-3 px-3 text-forest-700 font-medium">
                            {joinedDt ? joinedDt.toLocaleString('en-GH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                          </td>
                          <td className="py-3 px-3 text-forest-700 font-medium">
                            {isStillActive ? (
                              <span className="inline-flex items-center gap-1 text-[11px] text-green-700 font-semibold bg-green-50 px-2 py-0.5 rounded-full border border-green-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                Active in Call
                              </span>
                            ) : leftDt ? (
                              leftDt.toLocaleString('en-GH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="py-3 px-4 text-right font-semibold text-forest-800">
                            {r.durationMinutes > 0 ? `${r.durationMinutes} min${r.durationMinutes > 1 ? 's' : ''}` : '< 1 min'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-cream-200 bg-cream-50/40 flex items-center justify-between text-xs text-forest-500">
          <span>{uniqueAttendees} total participant{uniqueAttendees !== 1 ? 's' : ''} recorded</span>
          <button onClick={onClose} className="btn-secondary text-xs py-1.5 px-4">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CohortClassroomPage;
